import * as R from 'ramda';
import server from '../../../support/server';
import type { Rule, RuleBranchView, Status } from '../../../types/journeyTypes';
import QueryBuilder from '../QueryBuilder';
// TEMPLATE_TYPE_OPTIONS: Represents a modifiable view journey, with a
// percentage value so different than getCleanViewObject() in Journeys index
import { CHANNEL_RULE_FIELD, TEMPLATE_TYPE_OPTIONS } from './journeyConstants';

/* -------------------------------------------------------------------------

NOTE: These RuleUtils are more like actions than utils.
They get and save properties on rules stored in Redux (many are inpure and have side effects).

---------------------------------------------------------------------------*/

export function getCleanBranchView(): RuleBranchView {
  return {
    branch_view_id: '0',
    number_of_use: -1,
    percentage: 100,
  };
}

export function getCleanRuleObject() {
  var query = '+os:(android ios) +platform:(web) +event:(pageview)';
  var rule = {
    id: 'new',
    autotitled: true,
    query: query,
    number_of_use: -1,
    branchview_id: '',
    branch_views: [getCleanBranchView() as RuleBranchView],
    status: 'DRAFT' as Status,
    builder: new QueryBuilder(query),
    priority: 0,
    title: undefined,
  };
  return rule;
}

export function findRuleById(rule_list: Rule[], rule_id: string) {
  return R.find((rule) => rule.id === rule_id, rule_list);
}

export function getActiveRules(rule_list: Rule[]) {
  return R.filter((rule) => rule.status === 'ACTIVE', rule_list);
}

export function getActiveOrScheduledRules(ruleList) {
  return ruleList.filter((rule) => rule.status === 'ACTIVE' || rule.status === 'SCHEDULED');
}

export function freeTierLimitReached(rule_list, oneFreeJourney?: any) {
  if (oneFreeJourney) {
    return R.length(getActiveOrScheduledRules(rule_list)) >= 1;
  }
  return true;
}

export function listFreeTierTypes() {
  return TEMPLATE_TYPE_OPTIONS.filter((option) => option.premium === 0)
    .map((option) => option.value)
    .map((type) => type.split(' ').join('_').toUpperCase());
}

export function shouldShowTemplatesInTab(view, author) {
  if (view.hide_from_templates) {
    return false;
  }

  return author === 'user' ? parseInt(view.app_id, 10) !== 0 : parseInt(view.app_id, 10) === 0;
} // check if default tab or user tab}

export function passesAmpCompatibility(rule) {
  if (!rule) {
    return false; // default value is false
  }
  var builder = new QueryBuilder(rule.query);
  return builder.isAmpCompatible();
}

/*
 * @param {object} rule Object representing current rule of form
 * @return {array} Array of platforms as strings, or empty array
 */
export function getPlatforms(rule) {
  if (!rule) {
    return [];
  }
  var builder = new QueryBuilder(rule.query);
  const data = builder.getData();
  return data ? data.platform : [];
}

export function findNextCopyName(rule_list, previous_name) {
  if (!previous_name) {
    return '';
  }
  // two cases:
  //   1. previous_name is a copy
  //   2. previous_name is an original
  var baseName;
  var largestCopyValue;
  var match;
  var re;

  match = /^([^]+) \((\d)\)$/.exec(previous_name);
  if (match) {
    largestCopyValue = parseInt(match[2], 10);
    baseName = match[1];
  } else {
    largestCopyValue = 0;
    baseName = previous_name;
  }

  re = new RegExp(`^${baseName} \\((\\d+)\\)$`);

  // now find the largest value:
  largestCopyValue = R.reduce(
    (value, rule: Rule) => {
      match = re.exec(rule.title);
      if (!match) {
        return value;
      }

      var ruleValue = parseInt(match[1], 10);
      if (ruleValue < value) {
        return value;
      }

      return ruleValue;
    },
    largestCopyValue,
    rule_list
  );

  return `${baseName} (${largestCopyValue + 1})`;
}

export const getHighestPriority = R.reduce((acc, curr = {}) => {
  // @ts-ignore
  if (curr.status === 'ARCHIVED') {
    return acc;
  }
  // @ts-ignore
  if (curr.priority > acc) {
    // @ts-ignore
    return curr.priority;
  }
  return acc;
}, 0);

// Criteria is 1 active journey under free tier.
export function toggleFTStatus(app, enabled) {
  // @ts-ignore
  return server('toggle free tier', {
    app: app,
    enabled: enabled,
  });
}

export function combineViewsWithRule(branch_views, all_views_dictionary) {
  if (!all_views_dictionary) {
    return;
  }
  return branch_views.reduce(
    (dict, branch_view) => ({
      ...dict,
      [branch_view.branch_view_id]: all_views_dictionary[branch_view.branch_view_id],
    }),
    {}
  );
}

/* List of channels comes from combining:
   *
   * 1. channels associated with the linkCreator
   *    (what will show in QLC channel dropdown for instance)
   *
   * channelsFromLinkCreator: Array<{
       value: string,
       option: string
     }>
   *
   * 2. All channels on all Journeys this app has (from `rules`)
   * allJourneyRules:
            [{
              ...,
              channel: {
               value: string,
               option: string
              }
             }, ...]
   *
   * 3. The rule being created by the user for this specific Journey
   *    (we need the channel currently set on `rule`)
   * currentlyCreatedRule:
        { ...,
          channel: {
             value: string,
             option: string
          }
        }
   *
   * returns: Array<{
       value: string,
       option: string
     }>
   */
export function combineChannels(channelsFromLinkCreator, allJourneyRules, currentlyCreatedRule) {
  const currentlyCreatedChannel = {
    value: currentlyCreatedRule[CHANNEL_RULE_FIELD],
    label: currentlyCreatedRule[CHANNEL_RULE_FIELD],
  };
  let channelList = [...channelsFromLinkCreator];

  if (allJourneyRules && allJourneyRules.length) {
    channelList = allJourneyRules.reduce((channels, existingRule) => {
      if (existingRule[CHANNEL_RULE_FIELD]) {
        const channel = {
          value: existingRule[CHANNEL_RULE_FIELD],
          label: existingRule[CHANNEL_RULE_FIELD],
        };
        if (!R.contains(channel, channels)) {
          return [...channels, channel];
        }
      }
      return [...channels];
    }, channelList);
  }

  if (!R.contains(currentlyCreatedChannel, channelList)) {
    channelList = [...channelList, currentlyCreatedChannel];
  }

  return channelList;
}
